import { CrudAbstractModel } from "../crud-abstract/crud-abstract.model";
import { User } from "../user/user.model";
import { countryCodes } from "../shared/countries/country-codes.const";
import { ClientContactPerson } from "./client-contact-person-list/client-contact-person.model";
import { Tier } from "../tier/tier.type";
import { Location } from "app/location/location.model";
import { Company } from "app/company/company.model";

export interface ClientStats {
  total: number;
}

export class Client extends CrudAbstractModel {
  constructor(
    public id: number,
    private name: string,
    private users: User[],
    private companies: Company[],
    private contactPersons: ClientContactPerson[],
    private accountOwner: User | null,
    private ref: string,
    private tin: string,
    private street: string,
    private postalCode: string,
    private city: string,
    private country: string,
    private locale: string,
    private greetings: string,
    private hashtags: string,
    private status: string,
    private type: string,
    private forwarder: boolean,
    private paymentDays: number,
    private notes: string,
    private files: string[],
    private bank: string,
    private fclTier: Tier,
    private createdAt: Date,
    private updatedAt: Date,
    private lastQuoteDate: Date = null,
    private isEditable: boolean = false,
    private clientTermsLocations?: Location[],
  ) {
    super(id);
  }

  public getId(): number {
    return this.id;
  }

  public getName(): string {
    return this.name;
  }

  public setName(value: string) {
    this.name = value;
  }

  public getUsers(): User[] {
    return this.users;
  }

  public setUsers(param: User[]) {
    this.users = param;
  }

  public getCompanies(): Company[] {
    return this.companies;
  }

  public getFirstCompanyName(): string {
    return !!this.companies && this.companies.length > 0
      ? this.companies[0].name
      : "";
  }

  public get companyNames(): string {
    return this.companies?.map((company) => company.name).join(", ") ?? "";
  }

  public setCompanies(param: Company[]) {
    this.companies = param;
  }

  public getFirstContactPerson(): ClientContactPerson {
    return !!this.contactPersons && this.contactPersons.length > 0
      ? this.contactPersons[0]
      : null;
  }

  public getRef(): string {
    return this.ref;
  }

  public getTin(): string {
    return this.tin;
  }

  public getNotes(): string {
    return this.notes;
  }

  public setNotes(value): void {
    this.notes = value;
  }

  public getFiles(): string[] {
    return this.files;
  }

  public setFiles(value: string[]): void {
    this.files = value;
  }

  public getStreet(): string {
    return this.street;
  }

  public setStreet(value: string) {
    this.street = value;
  }

  public getPostalCode(): string {
    return this.postalCode;
  }

  public setPostalCode(value: string) {
    this.postalCode = value;
  }

  public getCity(): string {
    return this.city;
  }

  public setCity(value: string) {
    this.city = value;
  }

  public getCountry(): string {
    return this.country;
  }

  public setCountry(value: string) {
    this.country = value;
  }

  public getLocale(): string {
    return this.locale;
  }

  public getGreetings(): string {
    return this.greetings;
  }

  public getStatus(): string {
    return this.status;
  }

  public getType(): string {
    return this.type;
  }

  public getForwarder(): boolean {
    return this.forwarder;
  }

  public getPaymentDays(): number {
    return this.paymentDays;
  }

  public getBank(): string {
    return this.bank;
  }

  public getFclTier(): Tier {
    return this.fclTier;
  }

  public getAccountOwner(): User | null {
    return this.accountOwner;
  }

  public get accountOwnerName(): string {
    return this.accountOwner ? this.accountOwner.getFullName() : "";
  }

  public getCreatedAt(): Date {
    return this.createdAt;
  }

  public getUpdatedAt(): Date {
    return this.updatedAt;
  }

  public getHashtags(): string {
    return this.hashtags;
  }

  public setHashtags(value) {
    this.hashtags = value;
  }

  public getLastQuoteDate(): Date {
    return this.lastQuoteDate;
  }

  public get countryName() {
    return countryCodes[this.country];
  }

  public getIsEditable() {
    return this.isEditable;
  }

  public get contactPersonNames(): string {
    const names = [];
    if (this.contactPersons && this.contactPersons.length) {
      for (let i = 0; i < this.contactPersons.length; i++) {
        names.push(this.contactPersons[i].getName());
      }
    }

    return names.join(", ");
  }

  public toString() {
    return this.getName();
  }

  public getRequestParams() {
    return {
      name: this.name,
      users: this.users ? this.users.map((user) => user["id"]) : [],
      companies: this.companies
        ? this.companies.map((company) => company["id"])
        : [],
      ref: this.ref,
      tin: this.tin,
      street: this.street,
      postalCode: this.postalCode,
      city: this.city,
      country: this.country,
      locale: this.locale,
      hashtags: this.hashtags,
      greetings: this.greetings,
      status: this.status,
      type: this.type,
      fclTier: this.fclTier ? this.fclTier.getId() : null,
      accountOwner: this.accountOwner ? this.accountOwner.getId() : null,
      paymentDays: this.paymentDays,
      bank: this.bank,
      notes: this.notes,
      files: this.files,
    };
  }
}
